import {
  isNotNullOrEmptyString,
  singleOrArrayToArray,
} from '@dirootie-extensions/javascript';
import { SingleOrArray } from '@dirootie-extensions/typescript';
import { isArray, isString } from 'lodash';
import { stringUtils } from '../strings/string.utils';

type PathUtils = {
  builder: (param: SingleOrArray<string>, separator?: string) => IPathBuilder;
  buildSchemaVariableName: (entityName: string) => string;
};

export interface IPathBuilder {
  build: (sep?: string) => { result: string } & IPathBuilder;
  buildWithPredicate: (
    f: (x: string) => boolean,
    sep?: string
  ) => { result: string } & IPathBuilder;
  buildWithReplace(ptions: {
    replace: ((number | string) | [number | string, undefined | string])[];
    sep?: string;
  }): { result: string } & IPathBuilder;
  addSegment: (seg: string) => IPathBuilder;
}

const buildSchemaVariableName = (entityName: string) =>
  `get${stringUtils.titleCase(entityName)}ValidationSchema`;

const pathBuilder = (
  param: SingleOrArray<string>,
  separator: string = '.'
): IPathBuilder => {
  let segments = (
    (isString(param) && param.indexOf(separator) > -1
      ? param.split(separator)
      : param) as string[]
  ).filter((x) => isNotNullOrEmptyString(x));

  const addSegment = (seg: SingleOrArray<string>) => {
    const segArr = singleOrArrayToArray(seg);
    segments.concat(...segArr);
    return builder;
  };

  const build = (sep: string = separator) => {
    return { result: segments.join(sep), ...builder };
  };

  const buildWithPredicate = (f: (x: string) => boolean, sep?: string) => {
    return { result: segments.filter(f).join(sep), ...builder };
  };

  const buildWithReplace = (options: {
    replace: ((number | string) | [number | string, undefined | string])[];
    sep?: string;
  }) => {
    const { replace = [], sep = separator } = options;
    const aux = new Map<string | number, string | undefined>(
      replace.map((x) => (isArray(x) ? x : [x, undefined]))
    );
    const result: string[] = [];
    segments.forEach((x, i) => {
      if (aux.has(i)) {
        const replaceValue = aux.get(i);
        if (replaceValue !== undefined) {
          result.push(replaceValue);
        }
        return;
      }
      if (aux.has(x)) {
        const replaceValue = aux.get(x);
        if (replaceValue !== undefined) {
          result.push(replaceValue);
        }
        return;
      }
      result.push(x);
    });
    return { result: result.join(sep), ...builder };
  };

  const builder = {
    addSegment,
    build,
    buildWithPredicate,
    buildWithReplace,
    buildSchemaVariableName,
  };
  return builder;
};

const internalPathUtils = (): PathUtils => {
  return {
    builder: pathBuilder,
    buildSchemaVariableName,
  };
};

export const pathUtils = internalPathUtils();
